import { BuyOrderProductTypeModelType } from '@teinor/erp/types/company/order/buy/buyOrder/buyOrderProductType'
import { useMemo, useCallback, useState } from 'react'
import { ButtonSecondary, ButtonSecondaryOrLoader } from '../../../../../../../baseComponents/Button'
import { UserPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { DisplayPriceGettingParams } from '../../../../../../../baseComponents/Displays/DisplayPrice'
import { ModalBigCentered } from '../../../../../../../baseComponents/Modal'
import { ModalOpenedSetTy } from '../../../../../../../baseComponents/Modal/types'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { ITInputPercentage, ITInputPrice } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Inputs'
import { InputTableLeftColumnChange } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, InputTableLayoutData } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import TableWithoutVirtualListing from '../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { CustomRenderString, DeleteRow } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import FormRenderer from '../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { TextField } from '../../../../../../../forms/TextField'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getAllProductTypes } from '../../../../../../../utils/reducers/getters'
import { SetCompleteDataTy } from '../../../types'
import { parseOrderProductTypes } from './functions'
import { OrderProductType } from './types'

interface ModalProductTypesProps extends ModalEditComponentProps {
    isReadOnly?: boolean
    orderHeaderURLs?: OrderHeaderURLs
    updateHeaderData?: string
    headerData: TwinDictionary
    getCompleteData: () => void
    userPermissions?: UserPermissions
    setCompleteData?: SetCompleteDataTy
}

const ModalProductTypesOrder: React.FC<ModalProductTypesProps> = ({ isReadOnly, ...rest }) => {
    if (isReadOnly) {
        return <ModalProductTypesOrderReadOnly {...{ isReadOnly, ...rest }} />
    }
    return (<ModalProductTypesOrderEdit {...{ isReadOnly, ...rest }} />)
}

interface ModalProductTypesOrderReadOnlyProps {
    setOpened: ModalOpenedSetTy
    setCompleteData?: React.Dispatch<React.SetStateAction<InputTableLayoutData | null>>
    updateHeaderData?: string
    headerData?: TwinDictionary
}

const ModalProductTypesOrderReadOnly: React.FC<ModalProductTypesOrderReadOnlyProps> = ({ headerData, setOpened }) => {
    const { t } = useTwinTranslation()
    const dictProductTypes = useMemo(() => getAllProductTypes() || {}, [])
    const productTypes = headerData?.OrderProductTypes
    const currencyHeaderId = headerData?.CurrencyId
    const columns: ColumnTableSchema<TwinDictionary> = [
        {
            id: 'ProductTypeId',
            dataKey: 'ProductTypeId',
            label: t('productType', 'Tipo de producto'),
            customRender: (value, rowData) => <TextField label='' value={dictProductTypes?.[rowData.ProductTypeId]?.name} />
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            width: 120,
            customRender: (value) => <CustomRenderString value={value? value + ' %' : ''} />,
        },
        {
            id: 'price',
            dataKey: 'price',
            label: t('price', 'Precio'),
            customRender: (value) => <DisplayPriceGettingParams  className={'input_table_displaynumber_r '} value={parseFloat(String(value))} CurrencyId={currencyHeaderId} with_vat={false} />
        },
    ]
    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_product_type_buy gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='valuesProductTypes'>Valores Tipo de producto</TwinTrans></h2>
            <div className='mt-20 flex flex-auto flex-col'>
                <div className='flex-auto overflow-visible'>
                    <TableWithoutVirtualListing data={productTypes} columns={columns} className='min-h-300 add_product_type_table' />
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
        </ModalBigCentered>
    )
}

const ModalProductTypesOrderEdit: React.FC<ModalProductTypesProps> = ({ headerData, getCompleteData, orderHeaderURLs, setOpened }) => {
    const { t } = useTwinTranslation()
    const { myProductsTypes, deleteRow, myHandleAdd, myHandleProductType, dictProductTypes, loading, saveProductTypes } = useModalProductTypesEditLogic({ getCompleteData, headerData, orderHeaderURLs, setOpened })
    
    const currencyHeaderId = headerData.CurrencyId
    const keysDictLength = Object.keys(dictProductTypes)?.length
    const columns: ColumnTableSchema<BuyOrderProductTypeModelType> = [
        {
            id: 'ProductTypeId',
            dataKey: 'ProductTypeId',
            label: t('productType', 'Tipo de producto'),
            customRender: (value, rowData) => <TextField label='' value={dictProductTypes?.[rowData.ProductTypeId]?.name} />
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            width: 120,
            customRender: (value, rowData) => <ITInputPercentage value={value} dataKey={value} allRowData={{ ...rowData, id: rowData['ProductTypeId'] }} onChange={(rowIndex, field, value) => myHandleProductType(String(rowData.rowIndex), 'wasteWeight', value)} />,
        },
        {
            id: 'price',
            dataKey: 'price',
            label: t('price', 'Precio'),
            customRender: (value, rowData) => <ITInputPrice value={value} CurrencyId={currencyHeaderId} dataKey={value} allRowData={{...rowData, id: rowData['ProductTypeId']}} onChange={(rowIndex, field, value) =>myHandleProductType(String(rowData.rowIndex), 'price', value)} />,
        },
        {
            id: 'delete',
            dataKey: 'ProductTypeId',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData)} />,
        }
    ]

    const sections: FormRender<any> = []
    if (keysDictLength && keysDictLength !== myProductsTypes.ids?.length) {
        sections.push({
            cols: 1,
            elements: [
                {
                    name: 'add',
                    component: 'CustomSelectSearchBarWithNotInFilteredItems',
                    label: t('addValueProductType', 'Añadir valor tipo de producto'),
                    items: dictProductTypes,
                    className: 'items-start w-200 overflow-visible',
                    onChange: (value) => myHandleAdd(headerData?.['id'], 'add', value),
                    excludeItems: myProductsTypes.ids,
                },
            ]
        })
    }
    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_product_type_buy gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='addEditValueProductTypes'>Añadir o editar valor tipos de producto</TwinTrans></h2>
            <div className='flex flex-auto flex-col'>
                <div className='flex flex-col flex-auto gap-y-20 overflow-visible'>
                    <FormRenderer sections={sections} />
                    <TableWithoutVirtualListing data={myProductsTypes.productTypes} columns={columns} className='min-h-300 add_product_type_table' />
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondaryOrLoader buttonIsDisabled={loading} onClick={saveProductTypes} className='regular14 mt-10' textButton={t('save', 'Guardar')} />
                </div>
            </div>
        </ModalBigCentered>
    )
}

interface ModalProductTypesEditLogicProps {
    orderHeaderURLs?: OrderHeaderURLs
    headerData: TwinDictionary
    getCompleteData: () => void
    setOpened: ModalOpenedSetTy
}

const useModalProductTypesEditLogic = ({ headerData, orderHeaderURLs, setOpened, getCompleteData }: ModalProductTypesEditLogicProps) => {
    const [myProductsTypes, setMyProductsTypes] = useState<OrderProductType>(parseOrderProductTypes(headerData?.OrderProductTypes || []))
    const { startLoading, endLoading, loading } = useIsLoading()

    const dictProductTypes = useMemo(() => getAllProductTypes() || {}, [])

    const myHandleAdd: InputTableLeftColumnChange = useCallback(async (pos, field, value) => {
        setMyProductsTypes((old)=>{
            const copyOld= JSON.parse(JSON.stringify(old))
            const newElement = { OrderId: headerData.id, ProductTypeId: value, price: dictProductTypes[value].price || 0, wasteWeight: dictProductTypes[value].wasteWeight || 0 }
            if(!copyOld.productTypes.length){
                return { productTypes: [newElement], ids: [value]}
            }
            return { 
                productTypes: [...copyOld.productTypes, newElement],
                ids: [...copyOld.ids, value]
            }
        })

    }, [setMyProductsTypes, headerData.id, dictProductTypes])

    const deleteRow = useCallback(async (productType: any) => {
        setMyProductsTypes((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            const index = productType?.rowIndex
            copyOld.productTypes?.splice(index, 1)
            copyOld.ids?.splice(index, 1)
            return {...copyOld }
        })
    }, [setMyProductsTypes])

    const myHandleProductType: InputTableLeftColumnChange = useCallback(async (index, field, value) => {
        setMyProductsTypes((old) => {
        if (!old) {
            return null
        }
        const copyOld = JSON.parse(JSON.stringify(old))
        copyOld.productTypes[index] = {
            ...copyOld.productTypes[index], [field]: value,
        }
        return copyOld
    })
            
    }, [setMyProductsTypes])

    const saveProductTypes = useCallback(async () => {
        startLoading()
        if (orderHeaderURLs?.createUpdateProductTypes){
            const result = await twinFetchPostJSON(orderHeaderURLs?.createUpdateProductTypes, {
                productTypes: myProductsTypes.productTypes,
                ids: myProductsTypes.ids,
                OrderId: headerData.id
            })
            if(result){
                getCompleteData()
                setOpened(null)
            }
        }
        endLoading()
    }, [myProductsTypes, setOpened, startLoading, endLoading, getCompleteData, orderHeaderURLs?.createUpdateProductTypes, headerData.id])

    return { deleteRow, myHandleAdd, myHandleProductType, dictProductTypes, myProductsTypes, saveProductTypes, loading }
}


export default ModalProductTypesOrder